/**
 * NEXUS UI - BarChart
 * Vertical/horizontal bar chart with optional grouping and stacking
 */

import React, { useState, useMemo } from 'react';
import type { BaseProps, NexusVariant } from './types';

interface BarDataPoint {
  label: string;
  value: number;
  color?: NexusVariant;
}

interface BarGroup {
  label: string;
  values: { key: string; value: number; color?: NexusVariant }[];
}

export interface BarChartProps extends BaseProps {
  data: BarDataPoint[] | BarGroup[];
  variant?: NexusVariant;
  orientation?: 'vertical' | 'horizontal';
  stacked?: boolean;
  showValues?: boolean;
  showGrid?: boolean;
  showTooltip?: boolean;
  animated?: boolean;
  barRadius?: number;
  barGap?: number;
  height?: number;
  width?: number;
}

const variantColors: Record<NexusVariant, { base: string; glow: string }> = {
  cyan: { base: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { base: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { base: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { base: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { base: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { base: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

const isGroupedData = (data: BarDataPoint[] | BarGroup[]): data is BarGroup[] => {
  return data.length > 0 && 'values' in data[0];
};

export const BarChart: React.FC<BarChartProps> = ({
  data,
  variant = 'cyan',
  orientation = 'vertical',
  stacked = false,
  showValues = true,
  showGrid = true,
  showTooltip = true,
  animated = true,
  barRadius = 4,
  barGap = 8,
  height = 250,
  width = 400,
  className = '',
  style,
}) => {
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);
  const isHorizontal = orientation === 'horizontal';
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };

  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const { bars, maxValue, yTicks } = useMemo(() => {
    let maxVal = 0;
    const processedBars: {
      id: string;
      label: string;
      segments: { key: string; value: number; color: NexusVariant; offset: number }[];
      total: number;
    }[] = [];

    if (isGroupedData(data)) {
      data.forEach((group, groupIndex) => {
        if (stacked) {
          let offset = 0;
          const segments = group.values.map((v, i) => {
            const segment = {
              key: v.key,
              value: v.value,
              color: v.color || (['cyan', 'blue', 'violet', 'green', 'amber'] as NexusVariant[])[i % 5],
              offset,
            };
            offset += v.value;
            return segment;
          });
          const total = offset;
          maxVal = Math.max(maxVal, total);
          processedBars.push({ id: `group-${groupIndex}`, label: group.label, segments, total });
        } else {
          group.values.forEach((v, i) => {
            maxVal = Math.max(maxVal, v.value);
            processedBars.push({
              id: `${groupIndex}-${i}`,
              label: `${group.label} - ${v.key}`,
              segments: [{
                key: v.key,
                value: v.value,
                color: v.color || (['cyan', 'blue', 'violet', 'green', 'amber'] as NexusVariant[])[i % 5],
                offset: 0,
              }],
              total: v.value,
            });
          });
        }
      });
    } else {
      data.forEach((d, i) => {
        maxVal = Math.max(maxVal, d.value);
        processedBars.push({
          id: `bar-${i}`,
          label: d.label,
          segments: [{ key: d.label, value: d.value, color: d.color || variant, offset: 0 }],
          total: d.value,
        });
      });
    }

    // Calculate y-axis ticks
    const tickCount = 5;
    const yTickValues = Array.from({ length: tickCount }, (_, i) => (maxVal * i) / (tickCount - 1));

    return { bars: processedBars, maxValue: maxVal, yTicks: yTickValues };
  }, [data, variant, stacked]);

  const barWidth = isHorizontal
    ? chartHeight / bars.length - barGap
    : chartWidth / bars.length - barGap;

  const gradientId = `bar-gradient-${Math.random().toString(36).slice(2, 9)}`;
  const glowId = `bar-glow-${Math.random().toString(36).slice(2, 9)}`;

  const aspectRatio = width / height;

  return (
    <div
      className={`nx-bar-chart ${className}`}
      style={{ position: 'relative', width: '100%', ...style }}
    >
      <div style={{ position: 'relative', width: '100%', aspectRatio: `${aspectRatio}` }}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          onMouseLeave={() => setHoveredBar(null)}
        >
        <defs>
          {Object.entries(variantColors).map(([key, colors]) => (
            <linearGradient
              key={key}
              id={`${gradientId}-${key}`}
              x1="0%"
              y1="0%"
              x2={isHorizontal ? '100%' : '0%'}
              y2={isHorizontal ? '0%' : '100%'}
            >
              <stop offset="0%" stopColor={colors.base} stopOpacity="0.8" />
              <stop offset="100%" stopColor={colors.base} stopOpacity="1" />
            </linearGradient>
          ))}
          <filter id={glowId}>
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Grid lines */}
        {showGrid && (
          <g className="nx-bar-chart__grid">
            {yTicks.map((tick, i) => {
              const pos = isHorizontal
                ? padding.left + (tick / maxValue) * chartWidth
                : padding.top + chartHeight - (tick / maxValue) * chartHeight;

              return isHorizontal ? (
                <line
                  key={i}
                  x1={pos}
                  y1={padding.top}
                  x2={pos}
                  y2={padding.top + chartHeight}
                  stroke="rgba(0, 212, 255, 0.08)"
                  strokeWidth={1}
                />
              ) : (
                <line
                  key={i}
                  x1={padding.left}
                  y1={pos}
                  x2={padding.left + chartWidth}
                  y2={pos}
                  stroke="rgba(0, 212, 255, 0.08)"
                  strokeWidth={1}
                />
              );
            })}
          </g>
        )}

        {/* Y-axis */}
        <g className="nx-bar-chart__y-axis">
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={padding.top + chartHeight}
            stroke="rgba(0, 212, 255, 0.2)"
            strokeWidth={1}
          />
          {yTicks.map((tick, i) => {
            const y = isHorizontal
              ? padding.left + (tick / maxValue) * chartWidth
              : padding.top + chartHeight - (tick / maxValue) * chartHeight;

            return isHorizontal ? (
              <text
                key={i}
                x={y}
                y={padding.top + chartHeight + 16}
                textAnchor="middle"
                fill="var(--nx-text-ghost)"
                fontSize="10"
                fontFamily="var(--nx-font-mono)"
              >
                {Math.round(tick)}
              </text>
            ) : (
              <text
                key={i}
                x={padding.left - 8}
                y={y + 4}
                textAnchor="end"
                fill="var(--nx-text-ghost)"
                fontSize="10"
                fontFamily="var(--nx-font-mono)"
              >
                {Math.round(tick)}
              </text>
            );
          })}
        </g>

        {/* Bars */}
        {bars.map((bar, barIndex) => {
          const barX = isHorizontal
            ? padding.left
            : padding.left + barIndex * (barWidth + barGap) + barGap / 2;
          const barY = isHorizontal
            ? padding.top + barIndex * (barWidth + barGap) + barGap / 2
            : padding.top;

          return (
            <g key={bar.id}>
              {bar.segments.map((segment, segIndex) => {
                const colors = variantColors[segment.color];
                const segmentLength = (segment.value / maxValue) * (isHorizontal ? chartWidth : chartHeight);
                const segmentOffset = (segment.offset / maxValue) * (isHorizontal ? chartWidth : chartHeight);

                const x = isHorizontal ? barX + segmentOffset : barX;
                const y = isHorizontal ? barY : padding.top + chartHeight - segmentOffset - segmentLength;
                const w = isHorizontal ? segmentLength : barWidth;
                const h = isHorizontal ? barWidth : segmentLength;

                const isHovered = hoveredBar === `${bar.id}-${segIndex}`;

                return (
                  <g key={`${bar.id}-${segIndex}`}>
                    {/* Glow layer */}
                    <rect
                      x={x}
                      y={y}
                      width={Math.max(0, w)}
                      height={Math.max(0, h)}
                      rx={barRadius}
                      fill={colors.base}
                      opacity={0.3}
                      filter={`url(#${glowId})`}
                      style={{
                        transition: animated ? 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
                      }}
                    />
                    {/* Main bar */}
                    <rect
                      x={x}
                      y={y}
                      width={Math.max(0, w)}
                      height={Math.max(0, h)}
                      rx={barRadius}
                      fill={`url(#${gradientId}-${segment.color})`}
                      style={{
                        transition: animated ? 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
                        transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                        transformOrigin: `${x + w / 2}px ${y + h / 2}px`,
                      }}
                      onMouseEnter={() => setHoveredBar(`${bar.id}-${segIndex}`)}
                    />
                    {/* Value label */}
                    {showValues && !stacked && (
                      <text
                        x={isHorizontal ? x + w + 8 : x + w / 2}
                        y={isHorizontal ? barY + barWidth / 2 + 4 : y - 8}
                        textAnchor={isHorizontal ? 'start' : 'middle'}
                        fill={colors.base}
                        fontSize="11"
                        fontFamily="var(--nx-font-mono)"
                        fontWeight="600"
                      >
                        {segment.value}
                      </text>
                    )}
                  </g>
                );
              })}
              {/* X-axis label */}
              <text
                x={isHorizontal ? padding.left - 8 : barX + barWidth / 2}
                y={isHorizontal ? barY + barWidth / 2 + 4 : padding.top + chartHeight + 20}
                textAnchor={isHorizontal ? 'end' : 'middle'}
                fill="var(--nx-text-tertiary)"
                fontSize="10"
                fontFamily="var(--nx-font-interface)"
              >
                {bar.label.length > 10 ? bar.label.slice(0, 10) + '…' : bar.label}
              </text>
            </g>
          );
        })}
        </svg>
      </div>

      {/* Tooltip */}
      {showTooltip && hoveredBar && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'var(--nx-glass-dark)',
            border: '1px solid var(--nx-cyan-base)',
            borderRadius: '4px',
            padding: '8px 12px',
            boxShadow: '0 0 16px var(--nx-cyan-glow)',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--nx-font-mono)',
              fontSize: '12px',
              color: 'var(--nx-text-primary)',
            }}
          >
            {bars.find(b => b.segments.some((s, i) => `${b.id}-${i}` === hoveredBar))?.label}
          </div>
        </div>
      )}
    </div>
  );
};

export default BarChart;
